import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, vShow as _vShow, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withDirectives as _withDirectives, unref as _unref, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3b64f4cf"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "qr-code-scan" }
const _hoisted_2 = { class: "scan-wrapper" }
const _hoisted_3 = {
  key: 0,
  class: "scan-spinner"
}
const _hoisted_4 = {
  key: 1,
  class: "qr-textarea"
}
const _hoisted_5 = {
  key: 2,
  class: "set-camera-access",
  "data-testid": "camera-access"
}

import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import QrScanner from 'qr-scanner'

import { useAppStore } from '@/popup/store/AppStore'
import { Textarea } from '@/popup/components/shared/Form/Form'
import Button from '@/popup/components/shared/Button.vue'
import { useWeb3Eth } from '@/popup/compositions/useWeb3Eth'
import { logError, logInfo } from '@/shared/logger'
import { DBNotifications } from '@/shared/apis/bgnotifications'
import { useNotifications } from '@/popup/compositions/useNotifications'
import {
  EthUriReturnTypes,
  extractEthUrl,
  fromNumberExponentToLarge,
} from '@/popup/utils/ethUrlUtils'
import { ToastType } from '@/shared/models/AppNotification'
import { useCameraAccess } from '@/popup/compositions/useCameraAccess'
import { saveLastState } from '@/popup/storage/Database'
import SvgIcon from '@/popup/components/shared/SvgIcon.vue'
import Spinner from '@/popup/components/shared/Spinner.vue'
import Notification from '@/popup/components/shared/Notification.vue'
import {
  checkIfProfileIsImported,
  validateUpImport,
} from '@/popup/utils/upProfileValidation'
import { EoaAddress, UpAddress } from '@/shared/models/Profile'
import { assertNotUndefined, assertString } from '@/shared/validators'

type Props = {
  showQrCodeText: boolean
  info: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'QRCodeScan',
  props: {
    showQrCodeText: { type: Boolean },
    info: {}
  },
  setup(__props: any) {

const props = __props

const { getWeb3Eth } = useWeb3Eth()
const { cameraEnabled, setCameraAccess } = useCameraAccess()

const videoElement = ref<HTMLVideoElement | undefined>()
const qrScanner = ref<QrScanner | undefined>()
const qrCodeTextValue = ref()
const notifications = useNotifications()
const appStore = useAppStore()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })
const errorMessage = ref<string>()
const showSpinner = ref(false)
const onMountedRun = ref(false)
const focus = ref(false)

const executeTransaction = async (
  functionSignature: string,
  functionParameterTypes: string[],
  functionParameters: (string | string[])[],
  chainId: number,
  to?: UpAddress,
  value?: string
) => {
  const payload = getWeb3Eth()
    .abi.encodeParameters(functionParameterTypes, functionParameters)
    .slice(2)

  getWeb3Eth()
    .sendTransaction({
      from: appStore.currentProfile.address,
      to: to || appStore.currentProfile.address,
      data: functionSignature + payload,
      chainId,
      value: value && fromNumberExponentToLarge(value.trim().replace('/n', '')),
    })
    .on('error', error => {
      const message = error?.message ?? t('app.unknown_error')
      notifications.updateToast({
        id: appStore.currentProfile.address,
        message,
        profileAddress: appStore.currentProfile.address,
        method: DBNotifications.NOTIFICATION,
        system: false,
        type: ToastType.ERROR,
        isPersistent: false,
      })
    })

  await router.push('/')
}

const executeValueTransaction = async (
  value: string,
  to: UpAddress | EoaAddress,
  chainId: number
) => {
  getWeb3Eth()
    .sendTransaction({
      from: appStore.currentProfile.address,
      to,
      value: fromNumberExponentToLarge(value.trim().replace('/n', '')),
      chainId,
    })
    .on('error', error => {
      const message = error?.message ?? t('app.unknown_error')
      notifications.updateToast({
        id: appStore.currentProfile.address,
        message,
        profileAddress: appStore.currentProfile.address,
        method: DBNotifications.NOTIFICATION,
        system: false,
        type: ToastType.ERROR,
        isPersistent: false,
      })
    })

  await router.push('/')
}

const executeUPImport = async (address: UpAddress) => {
  const isValidImportOrError = await validateUpImport(address)
  if (!isValidImportOrError.success) {
    handleError(isValidImportOrError.error)
    return
  }
  const checkIfProfileImportedOrError = await checkIfProfileIsImported(address)
  if (!checkIfProfileImportedOrError.success) {
    handleError(checkIfProfileImportedOrError.error)
    return
  }
  router.push(`/qrcode/import-profile/${address}`)
}

const startScanning = async () => {
  if (!qrScanner.value && cameraEnabled.value && videoElement.value) {
    qrScanner.value = new QrScanner(
      videoElement.value,
      result => {
        if (result.data) {
          qrScanner.value && qrScanner.value.stop()
          process(result.data)
        }
      },
      {
        highlightScanRegion: false,
        highlightCodeOutline: false,
      }
    )
  }
  if (qrScanner.value) {
    try {
      const cameras = await QrScanner.listCameras(true)
      qrScanner.value.setCamera(cameras[0].id)
      await qrScanner.value.start()
      onMountedRun.value = true
    } catch {
      onMountedRun.value = false
    }
  }
}

const stopScanning = async () => {
  onMountedRun.value = false
  if (qrScanner.value) {
    qrScanner.value.destroy()
    qrScanner.value = undefined
  }
}

const process = async (text: string, isOnLoad?: boolean) => {
  try {
    const params = extractEthUrl(text, appStore.network.chainId)
    logInfo('extractedParams:', params)
    errorMessage.value = ''

    if (!params) {
      throw new Error(t('qr_code.scan.invalid_eth_url'))
    }

    if (params.chainId !== appStore.network.chainId) {
      throw new Error(t('qr_code.scan.invalid_chain_id'))
    }

    if (!isOnLoad) handleEthereumUrl(params)
  } catch (error: unknown) {
    handleError(error)
  }
}

const handleEthereumUrl = (params: EthUriReturnTypes) => {
  return {
    function: async () => {
      const {
        functionParameterTypes,
        functionParameters,
        functionSignature,
        chainId,
        address: to,
        value,
      } = params
      assertNotUndefined(
        functionParameterTypes,
        'Could not find function parameter types'
      )
      assertString(functionSignature)
      assertNotUndefined(
        functionParameters,
        'Could not find function parameters'
      )
      await executeTransaction(
        functionSignature,
        functionParameterTypes,
        functionParameters,
        chainId,
        to,
        value
      )
    },
    value: async () => {
      const { value, address: to, chainId } = params
      await executeValueTransaction(value as string, to as string, chainId)
    },
    address: async () => {
      const { address } = params
      await executeUPImport(address)
    },
  }[params.uriType]()
}

const handleError = async (error: unknown) => {
  logError(`Error in QRCodeScan:`, error)
  errorMessage.value = error instanceof Error ? error.message : 'Unknown error.'

  if (qrScanner.value) {
    await qrScanner.value.start()
  }
}

const onQrCodeSubmit = () => {
  process(qrCodeTextValue.value || '')
}

let timeout: NodeJS.Timeout | undefined
const activateScanner = (active: boolean) => {
  if (timeout) {
    clearTimeout(timeout)
  }
  timeout = setTimeout(() => {
    timeout = undefined
    if (active) {
      startScanning()
    } else {
      // If displaying text then don't scan
      stopScanning()
    }
  }, 10)
}

onMounted(async () => {
  showSpinner.value = true

  qrCodeTextValue.value =
    appStore.lastPopupState?.importQrCodeScanner?.qrCodeTextValue

  if (qrCodeTextValue.value) {
    process(qrCodeTextValue.value, true)
  }

  try {
    if (cameraEnabled.value && !props.showQrCodeText) {
      activateScanner(true)
    }
  } catch (error) {
    logError(`Error while activating scanner:`, error)
  } finally {
    showSpinner.value = false
  }
  focus.value = true
})

onUnmounted(() => {
  stopScanning()
})

const handleTextareaUpdate = async (value: string) => {
  qrCodeTextValue.value = value
  await saveLastState({
    importQrCodeScanner: {
      qrCodeTextValue: value,
      showQrCodeText: props.showQrCodeText,
    },
  })
}

watch(props, () => {
  // clear error when user toggle code/text view
  errorMessage.value = undefined
})
watch(() => cameraEnabled.value && !props.showQrCodeText, activateScanner)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (errorMessage.value)
      ? (_openBlock(), _createBlock(Notification, {
          key: 0,
          type: "error",
          class: "mb-4"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(errorMessage.value), 1 /* TEXT */)
          ]),
          _: 1 /* STABLE */
        }))
      : _createCommentVNode("v-if", true),
    _createElementVNode("div", _hoisted_2, [
      (showSpinner.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(Spinner)
          ]))
        : _createCommentVNode("v-if", true),
      _withDirectives(_createElementVNode("video", {
        ref_key: "videoElement",
        ref: videoElement,
        class: _normalizeClass(["qr-video", { loaded: onMountedRun.value }]),
        "data-testid": "video"
      }, null, 2 /* CLASS */), [
        [_vShow, !_ctx.showQrCodeText]
      ]),
      _withDirectives(_createElementVNode("div", null, [
        _createVNode(SvgIcon, {
          name: "scan-area",
          color: "var(--neutral-90)",
          "stroke-width": "4",
          class: "scan-area-top-left"
        }),
        _createVNode(SvgIcon, {
          name: "scan-area",
          color: "var(--neutral-90)",
          "stroke-width": "4",
          class: "scan-area-top-right"
        }),
        _createVNode(SvgIcon, {
          name: "scan-area",
          color: "var(--neutral-90)",
          "stroke-width": "4",
          class: "scan-area-bottom-left"
        }),
        _createVNode(SvgIcon, {
          name: "scan-area",
          color: "var(--neutral-90)",
          "stroke-width": "4",
          class: "scan-area-bottom-right"
        })
      ], 512 /* NEED_PATCH */), [
        [_vShow, qrScanner.value != null]
      ]),
      (_ctx.showQrCodeText)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("p", null, _toDisplayString(_ctx.info), 1 /* TEXT */),
            _createVNode(_unref(Textarea), {
              "textarea-class": "has-white-background",
              maxlength: 1000,
              value: qrCodeTextValue.value,
              placeholder: _ctx.$t('qr_code.scan.textarea_placeholder'),
              "has-autogrow": false,
              autofocus: focus.value,
              onOnEnter: onQrCodeSubmit,
              onOnUpdate: handleTextareaUpdate,
              onOnKeyDown: _cache[0] || (_cache[0] = ($event: any) => (errorMessage.value = undefined))
            }, null, 8 /* PROPS */, ["value", "placeholder", "autofocus"]),
            _createVNode(Button, {
              class: "mt-6",
              variant: "primary",
              "is-full-width": true,
              disabled: !qrCodeTextValue.value,
              onClick: onQrCodeSubmit
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('qr_code.scan.textarea_submit')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["disabled"])
          ]))
        : _createCommentVNode("v-if", true),
      (!_unref(cameraEnabled) && !_ctx.showQrCodeText)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(Button, {
              variant: "secondary",
              "is-full-width": true,
              onClick: _unref(setCameraAccess)
            }, {
              default: _withCtx(() => [
                _createVNode(SvgIcon, {
                  name: "camera",
                  color: "var(--neutral-20)",
                  class: "mr-2"
                }),
                _createTextVNode(" " + _toDisplayString(_ctx.$t('app.set_camera_access')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["onClick"])
          ]))
        : _createCommentVNode("v-if", true)
    ])
  ]))
}
}

})